<template>
  <div class="seckill-container">
    <div class="main-content">
      <el-row :gutter="20" type="flex" justify="center">
        <el-col :xs="24" :sm="22" :md="18" :lg="16">
          <el-card class="box-card" shadow="hover">
            <!-- Header 头部 Banner 氛围 -->
            <div slot="header" class="card-header">
              <div class="header-left">
                <span class="card-title">
                  <i class="el-icon-lightning fire-icon" /> 限时爆款秒杀
                </span>
                <el-tag type="danger" effect="dark" size="small" class="badge-tag">正品保障 · 限量特惠</el-tag>
              </div>
              <div class="header-right">
                <span class="status-indicator" :class="'status-' + seckillStatus">
                  {{ seckillStatus === 0 ? '未开始' : seckillStatus === 1 ? '进行中' : '已结束' }}
                </span>
              </div>
            </div>

            <el-row :gutter="30" class="product-detail-row">
              <!-- 商品主图区 -->
              <el-col :xs="24" :sm="10" :md="9">
                <div class="product-image-box">
                  <img :src="goods.picUrl || 'https://via.placeholder.com/300'" class="product-image" alt="商品主图">
                  <div v-if="goods.stock <= 0 && seckillStatus === 1" class="sold-out-mask">
                    <span>已售罄</span>
                  </div>
                </div>
              </el-col>

              <!-- 商品详情与抢购控制 -->
              <el-col :xs="24" :sm="14" :md="15" class="product-info-col">
                <h2 class="product-title">{{ goods.title || '加载中...' }}</h2>
                <p class="product-desc">高性能微服务实战专享示范商品，直降破冰价，先到先得！</p>

                <!-- 价格展示区 -->
                <div class="price-panel">
                  <div class="price-left">
                    <span class="seckill-price-label">秒杀价</span>
                    <span class="currency">￥</span>
                    <span class="seckill-price">{{ goods.seckillPrice }}</span>
                    <span class="original-price">￥{{ goods.originalPrice }}</span>
                  </div>
                  <div class="price-right" v-if="goods.seckillLimit">
                    <span class="limit-tag">每人限购 {{ goods.seckillLimit }} 件</span>
                  </div>
                </div>

                <!-- 剩余库存进度条 -->
                <div class="stock-panel">
                  <div class="stock-header">
                    <span class="label">抢购进度：</span>
                    <span class="stock-text">仅剩 <strong>{{ goods.stock }}</strong> 件</span>
                  </div>
                  <el-progress
                    :percentage="stockPercentage"
                    :status="stockPercentage < 20 ? 'exception' : 'success'"
                    :stroke-width="14"
                    :show-text="false"
                    class="custom-progress"
                  />
                </div>

                <!-- 交互控制面板 -->
                <div class="action-panel">
                  <!-- 未开始：倒计时组件 -->
                  <div v-if="seckillStatus === 0" class="countdown-box">
                    <div class="countdown-title"><i class="el-icon-time" /> 距离秒杀开启还有</div>
                    <div class="countdown-timer">{{ countdownText }}</div>
                  </div>

                  <!-- 进行中 / 已抢购 / 售罄 -->
                  <div v-else class="btn-group">
                    <el-button
                      type="danger"
                      size="medium"
                      class="seckill-btn"
                      :class="{ 'pulse-anim': seckillStatus === 1 && goods.stock > 0 && !hasPurchased }"
                      :disabled="seckillStatus === 2 || goods.stock <= 0 || loading || hasPurchased"
                      :loading="loading"
                      @click="handleSeckill"
                    >
                      <i v-if="!loading" class="el-icon-lightning" />
                      {{ btnText }}
                    </el-button>
                  </div>
                </div>
              </el-col>
            </el-row>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 排队等待 Dialog -->
    <el-dialog
      title="正在全力抢购中"
      :visible.sync="queuingDialogVisible"
      width="380px"
      :close-on-click-modal="false"
      :show-close="false"
      center
      custom-class="queue-dialog"
    >
      <div class="queue-box">
        <el-progress type="circle" :percentage="queueProgress" status="success" :width="100" />
        <p class="queue-tip">系统高并发处理中，请勿刷新页面...</p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { doSeckill, getProductDetail, getSeckillPath, getSeckillResult } from '@/api/mall'

export default {
  name: 'SeckillPage',
  data() {
    return {
      userId: 0,
      skuId: null,
      loading: false,
      hasPurchased: false,
      goods: {
        title: '',
        picUrl: '',
        originalPrice: '0.00',
        seckillPrice: '0.00',
        stock: 0,
        totalStock: 0,
        seckillLimit: 1,
        startTime: new Date().getTime(),
        endTime: new Date().getTime()
      },
      seckillStatus: 0, // 0: 未开始, 1: 进行中, 2: 已结束
      countdownText: '00小时 00分 00秒',
      timer: null,
      queuingDialogVisible: false,
      queueProgress: 20,
      pollTimer: null
    }
  },
  computed: {
    stockPercentage() {
      if (!this.goods.totalStock || this.goods.totalStock <= 0) return 0
      const pct = Math.floor((this.goods.stock / this.goods.totalStock) * 100)
      return Math.min(100, Math.max(0, pct))
    },
    btnText() {
      if (this.hasPurchased) return '您已成功抢购'
      if (this.seckillStatus === 0) return '秒杀未开始'
      if (this.goods.stock <= 0 || this.seckillStatus === 2) return '已售罄'
      return '立即抢购'
    }
  },
  created() {
    const userInfo = this.$store.state.userInfo
    if (userInfo) this.userId = userInfo.userId
    this.skuId = this.$route.query.skuId || this.$route.params.skuId || 1
  },
  mounted() {
    this.checkUserOrderStatus()
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    async checkUserOrderStatus() {
      try {
        const resp = await getSeckillResult({ userId: this.userId, skuId: this.skuId })
        if (resp.code === 0 && resp.data) {
          this.hasPurchased = true
          this.$message.success('查到已有抢购订单，正在跳转...')
          setTimeout(() => this.redirectToOrderPay(resp.data), 1000)
          return
        }
      } catch (e) {
        console.error('前置订单检查异常', e)
      }
      this.getData()
    },
    getData() {
      getProductDetail(this.skuId).then(resp => {
        if (resp.code === 0 && resp.data) {
          this.goods = resp.data
          this.initCountdown()
        } else {
          this.$message.error(resp.msg || '获取商品详情失败')
        }
      })
    },
    initCountdown() {
      const updateTimer = () => {
        const now = new Date().getTime()
        const startDiff = this.goods.startTime - now
        const endDiff = this.goods.endTime - now

        if (startDiff > 0) {
          this.seckillStatus = 0
          const s = Math.floor((startDiff / 1000) % 60).toString().padStart(2, '0')
          const m = Math.floor((startDiff / (1000 * 60)) % 60).toString().padStart(2, '0')
          const h = Math.floor(startDiff / (1000 * 60 * 60)).toString().padStart(2, '0')
          this.countdownText = `${h}小时 ${m}分 ${s}秒`
        } else if (endDiff > 0) {
          this.seckillStatus = 1
          if (this.timer) clearInterval(this.timer)
        } else {
          this.seckillStatus = 2
          if (this.timer) clearInterval(this.timer)
        }
      }
      updateTimer()
      this.timer = setInterval(updateTimer, 1000)
    },
    async handleSeckill() {
      if (this.hasPurchased) return
      this.loading = true
      try {
        const pathResp = await getSeckillPath({ skuId: this.skuId, userId: this.userId })
        if (pathResp.code === 0) {
          const path = pathResp.data
          const seckillResp = await doSeckill(path, { userId: this.userId, skuId: this.skuId, quantity: 1 })
          if (seckillResp.code === 0) {
            this.startPollingOrder()
          } else {
            this.$message.error(seckillResp.msg || '抢购人数过多，请稍重试')
            this.loading = false
          }
        } else {
          this.$message.error(pathResp.msg || '获取秒杀通道失败')
          this.loading = false
        }
      } catch (err) {
        this.$message.error('网络繁忙，请稍后再试')
        this.loading = false
      }
    },
    startPollingOrder() {
      this.queuingDialogVisible = true
      this.queueProgress = 25
      let count = 0
      this.pollTimer = setInterval(async () => {
        count++
        this.queueProgress = Math.min(92, this.queueProgress + 12)
        try {
          const resp = await getSeckillResult({ userId: this.userId, skuId: this.skuId })
          if (resp.code === 0 && resp.data) {
            this.queueProgress = 100
            clearInterval(this.pollTimer)
            setTimeout(() => {
              this.queuingDialogVisible = false
              this.loading = false
              this.hasPurchased = true
              this.redirectToOrderPay(resp.data)
            }, 400)
          } else if (count > 12) {
            clearInterval(this.pollTimer)
            this.queuingDialogVisible = false
            this.loading = false
            this.$message.warning('排队人数较多，请前往“我的订单”查看')
          }
        } catch (e) {
          console.error('轮询异常', e)
        }
      }, 1200)
    },
    redirectToOrderPay(orderId) {
      this.$router.push({ path: '/mall/order/pay', query: { orderId, skuId: this.skuId } })
    },
    clearTimers() {
      if (this.timer) clearInterval(this.timer)
      if (this.pollTimer) clearInterval(this.pollTimer)
    }
  }
}
</script>

<style scoped>
.seckill-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #fef2f2 0%, #eef2ff 100%);
  padding: 30px 15px;
}

.box-card {
  border-radius: 12px;
  border: none;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 20px;
  font-weight: 800;
  color: #111827;
}

.fire-icon {
  color: #ef4444;
  margin-right: 4px;
}

.badge-tag {
  margin-left: 12px;
  border-radius: 4px;
}

.status-indicator {
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
}
.status-0 { background-color: #fef3c7; color: #d97706; }
.status-1 { background-color: #d1fae5; color: #059669; }
.status-2 { background-color: #f3f4f6; color: #9ca3af; }

.product-image-box {
  position: relative;
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff;
  text-align: center;
}

.product-image {
  width: 100%;
  height: 280px;
  object-fit: cover;
  display: block;
}

.sold-out-mask {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: bold;
  letter-spacing: 2px;
}

.product-title {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.product-desc {
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 16px;
}

.price-panel {
  background: linear-gradient(90deg, #fff1f2 0%, #fff 100%);
  border-left: 4px solid #ef4444;
  padding: 12px 16px;
  border-radius: 0 8px 8px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.seckill-price-label {
  color: #dc2626;
  font-size: 13px;
  font-weight: 600;
  margin-right: 6px;
}

.currency {
  color: #dc2626;
  font-weight: bold;
  font-size: 16px;
}

.seckill-price {
  color: #dc2626;
  font-size: 32px;
  font-weight: 800;
  margin-right: 12px;
}

.original-price {
  color: #9ca3af;
  text-decoration: line-through;
  font-size: 14px;
}

.limit-tag {
  font-size: 12px;
  color: #b91c1c;
  background: #fee2e2;
  padding: 2px 8px;
  border-radius: 4px;
}

.stock-panel {
  margin-bottom: 24px;
}

.stock-header {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 6px;
}

.stock-text strong {
  color: #ef4444;
}

.countdown-box {
  background: #1f2937;
  color: #fff;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
}

.countdown-title {
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 4px;
}

.countdown-timer {
  font-size: 22px;
  font-weight: bold;
  color: #f59e0b;
  letter-spacing: 1px;
}

.seckill-btn {
  width: 100%;
  height: 48px;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 1px;
  border-radius: 8px;
  background-color: #ef4444;
  border-color: #ef4444;
}

.pulse-anim {
  animation: pulse 1.8s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
  70% { box-shadow: 0 0 0 12px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}

.queue-box {
  text-align: center;
  padding: 10px 0;
}

.queue-tip {
  margin-top: 16px;
  color: #4b5563;
  font-size: 14px;
}
</style>
